/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Webcam from 'react-webcam';

import {
  hooks as orderHooks,
  selectors as orderSelectors,
  actions as orderActions,
} from '@/domains/esim/order';
import {
  selectors as recognitionSelectors,
  actions as recognitionAction,
} from '@/domains/esim/recognation';
import { actions as viewActions } from '@/domains/view';
import { CloseMobile } from '@/icons/index';
import { Loader } from '@/ui/index';

import RecognitionRules from './RecognationRules';
import RecognitionWebcam from './RecognationWebcam';
import Loading from './Loading';
import Pending from './Pending';
import Error from './Error';
import Timer from './CountDown';

import styles from './RecordModal.module.scss';

const RecordModal = ({
  active = false,
  setActive = () => null,
  className,
  classNameInner,
  setActiveTab,
  routePath,
  countDown,
  progressBarTime,
}) => {
  const videoConstraints = {
    width: 540,
    facingMode: 'user',
  };
  const OVERRAL_PROGRESSBAR_TIME = progressBarTime || 20;

  const closeModal = (event) => {
    if (event.key === 'Escape') {
      setActive(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', closeModal);

    return () => {
      document.removeEventListener('keydown', closeModal);
    };
  }, [closeModal]);

  const dispatch = useDispatch();

  // STORE
  const {
    order: { trackId, formData },
    recognition: { capturing, error, loading, pending, pollingInterval },
  } = useSelector((state) => ({
    order: orderSelectors.selectOrder(state),
    recognition: recognitionSelectors.getRecognitionState(state),
  }));

  // STATE
  const [progress, setProgress] = useState(2);
  const [seconds, setSeconds] = React.useState(0);
  const router = useRouter();

  // REFS
  const countDownRef = useRef(null);
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const timeoutID = useRef(null);

  // APIS
  const [mutation, { isSuccess }] = orderHooks.useFaceRecognitionMutation();
  const { isSuccess: faceRecResult, data } =
    orderHooks.useGetFaceRecognitionResult(trackId, {
      skip: !isSuccess,
      pollingInterval,
    });

  const postRulesData = useMemo(
    () => ({
      fin_code: formData?.pin,
      passport_series: formData?.countyCode?.name + formData?.countyCodeValue,
      track_id: trackId,
      interested_phone_number: formData?.selectedNumber,
      timestamp: new Date(),
      app_name: 'bakcell_web',
    }),
    [trackId, formData],
  );
  const { data: recognitionRules, isLoading: recognitionRulesLoading } =
    orderHooks.useGetRecognitionRules(postRulesData);

  const progressInterval = useMemo(() => {
    return (
      OVERRAL_PROGRESSBAR_TIME /
      (OVERRAL_PROGRESSBAR_TIME ||
        recognitionRules?.data?.countdown ||
        countDown)
    );
  }, [recognitionRules]);

  const handleStartCountdown = () => {
    if (countDownRef?.current) {
      countDownRef.current?.start();
    }
  };
  const handlePollingStatus = () => {
    dispatch(recognitionAction.setLoading(true));
  };

  const handleStartCaptureClick = useCallback(() => {
    dispatch(recognitionAction.setCapturing(true));
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream);
    mediaRecorderRef.current.addEventListener(
      'dataavailable',
      handleDataAvailable,
    );
    mediaRecorderRef.current.start();
  }, [webcamRef, capturing, mediaRecorderRef]);

  const handleDataAvailable = useCallback(({ data }) => {
    if (data.size > 0) {
      const blob = new Blob([data]);
      const file = new FormData();
      file.append('file', blob, 'video.webm');

      mutation({
        trackId,
        file,
      });

      handlePollingStatus();
      setProgress(0);
    }
  }, []);

  const handleStopCaptureClick = useCallback(() => {
    if (mediaRecorderRef.current) mediaRecorderRef.current.stop();

    dispatch(recognitionAction.setCapturing(false));
  }, [mediaRecorderRef, webcamRef, capturing, progress]);

  const handleStartRecognition = useCallback(() => {
    handleStartCountdown();
    handleStartCaptureClick();
    setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 100) {
          return 100;
        }
        setSeconds((prev) => prev + 0.1);
        return oldProgress + progressInterval;
      });
    }, OVERRAL_PROGRESSBAR_TIME * 10);
  }, [progressInterval]);
  // LISTENERS
  useEffect(() => {
    if (data?.code && data?.code !== 401) {
      timeoutID.current = setTimeout(() => {
        setActive(false);
        dispatch(recognitionAction.setLoading(false));
        dispatch(recognitionAction.setPollingInterval(0));
        dispatch(orderActions.setActiveSima(true));
      }, 20000);
    }
    if (faceRecResult) {
      if (data.code === 102) {
        return;
      }
      dispatch(recognitionAction.setLoading(false));
      dispatch(recognitionAction.setPollingInterval(9999999999));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'facesuccess',
      });

      if (data.code === 401) {
        dispatch(recognitionAction.setError(true));
        dispatch(viewActions.closeRecognitionModal());
        return;
      }

      if (data.code === 200) {
        dispatch(recognitionAction.setPending(true));
        setTimeout(() => {
          setActiveTab();
          dispatch(recognitionAction.setPending(false));
          dispatch(viewActions.closeRecognitionModal());
        }, 3000);
      }
    }
    return () => clearTimeout(timeoutID.current);
  }, [faceRecResult, data]);

  useEffect(() => {
    if (progress > 99 && mediaRecorderRef.current?.state !== 'inactive') {
      handleStopCaptureClick();
    }
  }, [progress]);

  if (recognitionRulesLoading) {
    return <Loader />;
  }

  const redirectToHome = () => {
    if (error) {
      router.replace(routePath);
    }
  };

  // UI
  const getContent = () => {
    switch (true) {
      case loading:
        return <Loading />;
      case pending:
        return <Pending />;
      case error:
        return <Error />;
      default:
        return (
          <RecognitionWebcam
            {...{
              capturing,
              handleStartRecognition,
              progress,
              recognitionRules,
            }}
          >
            {webcamRef && (
              <Webcam
                audio={false}
                ref={webcamRef}
                height='100%'
                width='100%'
                videoConstraints={videoConstraints}
                playsInline
                mirrored
                className={styles.webcam}
              />
            )}
            <Timer
              timer={recognitionRules?.data?.countdown}
              ref={countDownRef}
              className={styles.timer}
            />
            {recognitionRules?.data?.actions?.map((item) => {
              return (
                <>
                  {seconds + 2 > item?.start && seconds <= item.end && (
                    <RecognitionRules action={item.action} />
                  )}
                  ``
                </>
              );
            })}
          </RecognitionWebcam>
        );
    }
  };

  return (
    <>
      <div
        className={classNames([
          styles.wrap,
          { [styles.open]: active },
          className,
        ])}
        itemScope
        itemType='https://schema.org/Property'
      >
        <div className={classNames([styles.faceInfoInner, classNameInner])}>
          <div className={styles.faceInfoContent}>
            <div
              className={styles.close}
              onClick={() => {
                setActive(false);
                redirectToHome();
              }}
            >
              <CloseMobile />
            </div>
            {getContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default RecordModal;
